import { formatDateWithoutTimezone, loadGuarantorsFromParticipants, loadFees } from '../helpers/operationHelpers';
import { formatLocalDateToBR } from '../../../../../utils/DateUtils';
import { mapReceivablesFromSchema } from './dataMappers';
import { formatISOToDate, formatInstallments } from '../formatters/formatters';
import { EMPTY_BILLING_CONFIG } from '../../components/form/BillingConfigSection';
import { extractContractsAsReceivablesFromSchema, extractContractGuaranteeFromSchema } from './contractDataHelpers';
import { ContractApiResponse, ParticipantResponse } from '../../types/api-responses';
import { AcknowledgementDebtForm, StructuredOperationForm, Collaterals, CollateralsWithReceivables, FiduciaryAlienation } from '../../types/operations';

const isoToDateBR = (value: string | undefined | null): string => {
    if (!value) return '';
    return formatLocalDateToBR(value);
};

// ============================================
// COLLATERALS EXTRACTION — retrocompat helpers
// ============================================

/**
 * Extracts receivables from either root level (new format for cessão),
 * collaterals format, or legacy format.
 */
const extractReceivables = (schema: any) => {
    // New format: receivables at root level of operation_dictionary (cessão)
    if (schema.receivables?.items) {
        return schema.receivables;
    }
    // Previous format: inside collaterals.assets
    if (schema.collaterals?.assets?.receivables) {
        return schema.collaterals.assets.receivables;
    }
    // Legacy format: mapped from duplicates/receivables fields
    return mapReceivablesFromSchema(schema);
};

/**
 * Extracts contract guarantee items from root level (new format for cessão),
 * collaterals format, or legacy format.
 * Returns array of contract guarantee items.
 */
const extractContractGuaranteeItems = (schema: any): any[] => {
    // Usa o extrator aninhado (agrupa por contrato e monta installments[]),
    // com fallback do formato plano legado. Retorna itens no modelo aninhado.
    const state = extractContractGuaranteeFromSchema(schema);
    return state?.items || [];
};

/**
 * Extracts inventory items from collaterals or maps from legacy fiduciary_alienation.
 */
const extractInventoryItems = (schema: any): any[] => {
    if (schema.collaterals?.assets?.inventory?.items) {
        return schema.collaterals.assets.inventory.items;
    }
    if (schema.fiduciary_alienation) {
        return (Array.isArray(schema.fiduciary_alienation) ? schema.fiduciary_alienation : [])
            .filter((item: any) => item.name === 'Estoque' || item.name === 'Grãos')
            .map((item: any) => ({
                collateral_id: crypto.randomUUID?.() || `legacy-${Date.now()}-${Math.random()}`,
                legal_regime: item.guarantee_type || 'AF',
                asset_type: item.name === 'Grãos' ? 'GRAIN' : 'INPUT',
                name: item.name,
                value: item.value || 0,
                description: item.description || '',
            }));
    }
    return [];
};

/**
 * Extracts machinery items from collaterals or maps from legacy fiduciary_alienation.
 */
const extractMachineryItems = (schema: any): any[] => {
    if (schema.collaterals?.assets?.machinery?.items) {
        return schema.collaterals.assets.machinery.items;
    }
    if (schema.fiduciary_alienation) {
        return (Array.isArray(schema.fiduciary_alienation) ? schema.fiduciary_alienation : [])
            .filter((item: any) => item.name === 'Maquinário')
            .map((item: any) => ({
                collateral_id: crypto.randomUUID?.() || `legacy-${Date.now()}-${Math.random()}`,
                legal_regime: item.guarantee_type || 'AF',
                asset_type: 'OTHER',
                name: item.name,
                value: item.value || 0,
                description: item.description || '',
            }));
    }
    return [];
};

/**
 * Extracts real estate items from collaterals or maps from legacy fiduciary_alienation.
 */
const extractRealEstateItems = (schema: any): any[] => {
    if (schema.collaterals?.assets?.real_estate?.items) {
        return schema.collaterals.assets.real_estate.items;
    }
    if (schema.fiduciary_alienation) {
        return (Array.isArray(schema.fiduciary_alienation) ? schema.fiduciary_alienation : [])
            .filter((item: any) => item.name === 'Imóvel')
            .map((item: any) => ({
                collateral_id: crypto.randomUUID?.() || `legacy-${Date.now()}-${Math.random()}`,
                legal_regime: item.guarantee_type || 'AF',
                property_type: 'RURAL',
                name: item.name,
                value: item.value || 0,
                description: item.description || '',
            }));
    }
    return [];
};

/**
 * Extracts securities items from collaterals (no legacy equivalent).
 */
const extractSecuritiesItems = (schema: any): any[] => {
    if (schema.collaterals?.assets?.securities?.items) {
        return schema.collaterals.assets.securities.items;
    }
    return [];
};

/**
 * Builds Collaterals object (without receivables/contracts) for Cessão de Recebíveis.
 * Only contains: inventory, machinery, real_estate, securities.
 */
export const buildCollateralsOnlyFromSchema = (schema: any): Collaterals => {
    const inventoryItems = schema.collaterals?.assets?.inventory?.items || extractInventoryItems(schema);
    const machineryItems = schema.collaterals?.assets?.machinery?.items || extractMachineryItems(schema);
    const realEstateItems = schema.collaterals?.assets?.real_estate?.items || extractRealEstateItems(schema);
    const securitiesItems = schema.collaterals?.assets?.securities?.items || extractSecuritiesItems(schema);

    const inventoryTotal = inventoryItems.reduce((sum: number, i: any) => sum + (i.value || 0), 0);
    const machineryTotal = machineryItems.reduce((sum: number, i: any) => sum + (i.value || 0), 0);
    const realEstateTotal = realEstateItems.reduce((sum: number, i: any) => sum + (i.value || 0), 0);
    const securitiesTotal = securitiesItems.reduce((sum: number, i: any) => sum + (i.initial_value || i.value || 0), 0);

    const totalValue = inventoryTotal + machineryTotal + realEstateTotal + securitiesTotal;
    const totalQuantity = inventoryItems.length + machineryItems.length + realEstateItems.length + securitiesItems.length;

    return {
        total_value: totalValue,
        total_quantity: totalQuantity,
        assets: {
            inventory: { items: inventoryItems, total: inventoryTotal, quantity: inventoryItems.length },
            machinery: { items: machineryItems, total: machineryTotal, quantity: machineryItems.length },
            real_estate: { items: realEstateItems, total: realEstateTotal, quantity: realEstateItems.length },
            securities: { items: securitiesItems, total: securitiesTotal, quantity: securitiesItems.length },
        }
    };
};

/**
 * Builds full CollateralsWithReceivables object from API response schema (supports both formats).
 * Used by StructuredOperation and AcknowledgementDebt where receivables/contracts ARE collateral.
 */
export const buildCollateralsFromSchema = (schema: any): CollateralsWithReceivables => {
    if (schema.collaterals) {
        return {
            total_value: schema.collaterals.total_value || 0,
            total_quantity: schema.collaterals.total_quantity || 0,
            assets: {
                receivables: extractReceivables(schema),
                contracts: {
                    items: extractContractGuaranteeItems(schema),
                    total: schema.collaterals.assets?.contracts?.total || 0,
                    quantity: schema.collaterals.assets?.contracts?.quantity || 0,
                },
                inventory: {
                    items: schema.collaterals.assets?.inventory?.items || [],
                    total: schema.collaterals.assets?.inventory?.total || 0,
                    quantity: schema.collaterals.assets?.inventory?.quantity || 0,
                },
                machinery: {
                    items: schema.collaterals.assets?.machinery?.items || [],
                    total: schema.collaterals.assets?.machinery?.total || 0,
                    quantity: schema.collaterals.assets?.machinery?.quantity || 0,
                },
                real_estate: {
                    items: schema.collaterals.assets?.real_estate?.items || [],
                    total: schema.collaterals.assets?.real_estate?.total || 0,
                    quantity: schema.collaterals.assets?.real_estate?.quantity || 0,
                },
                securities: {
                    items: schema.collaterals.assets?.securities?.items || [],
                    total: schema.collaterals.assets?.securities?.total || 0,
                    quantity: schema.collaterals.assets?.securities?.quantity || 0,
                },
            }
        };
    }

    // Legacy format — build collaterals from separate fields
    const receivables = mapReceivablesFromSchema(schema);
    const contractItems = extractContractGuaranteeItems(schema);
    const inventoryItems = extractInventoryItems(schema);
    const machineryItems = extractMachineryItems(schema);
    const realEstateItems = extractRealEstateItems(schema);

    const contractsTotal = contractItems.reduce((sum: number, i: any) => sum + (i.contract_value || 0), 0);
    const inventoryTotal = inventoryItems.reduce((sum: number, i: any) => sum + (i.value || 0), 0);
    const machineryTotal = machineryItems.reduce((sum: number, i: any) => sum + (i.value || 0), 0);
    const realEstateTotal = realEstateItems.reduce((sum: number, i: any) => sum + (i.value || 0), 0);

    const totalValue = (receivables.total || 0) + contractsTotal + inventoryTotal + machineryTotal + realEstateTotal;
    const totalQuantity = (receivables.quantity || 0) + contractItems.length + inventoryItems.length + machineryItems.length + realEstateItems.length;

    return {
        total_value: totalValue,
        total_quantity: totalQuantity,
        assets: {
            receivables,
            contracts: { items: contractItems, total: contractsTotal, quantity: contractItems.length },
            inventory: { items: inventoryItems, total: inventoryTotal, quantity: inventoryItems.length },
            machinery: { items: machineryItems, total: machineryTotal, quantity: machineryItems.length },
            real_estate: { items: realEstateItems, total: realEstateTotal, quantity: realEstateItems.length },
            securities: { items: [], total: 0, quantity: 0 },
        }
    };
};

/**
 * Extracts legacy fiduciary_alienation array for backward compat with FiduciaryAlienationManager
 * until full component migration is done.
 */
export const extractLegacyFiduciaryAlienation = (schema: any): FiduciaryAlienation[] => {
    if (schema.collaterals?.assets) {
        const assets = schema.collaterals.assets;
        const result: FiduciaryAlienation[] = [];
        (assets.inventory?.items || []).forEach((i: any) => {
            result.push({ name: i.asset_type === 'GRAIN' ? 'Grãos' : 'Estoque', value: i.value || 0, description: i.description, guarantee_type: i.legal_regime || 'AF' });
        });
        (assets.machinery?.items || []).forEach((i: any) => {
            result.push({ name: 'Maquinário', value: i.value || 0, description: i.description, guarantee_type: i.legal_regime || 'AF' });
        });
        (assets.real_estate?.items || []).forEach((i: any) => {
            result.push({ name: 'Imóvel', value: i.value || 0, description: i.description, guarantee_type: i.legal_regime || 'AF' });
        });
        return result;
    }
    if (schema.fiduciary_alienation) {
        return (Array.isArray(schema.fiduciary_alienation) ? schema.fiduciary_alienation : []).map((item: any) => ({
            name: item.name || '',
            value: item.value || 0,
            description: item.description || undefined,
            guarantee_type: item.guarantee_type || 'AF'
        }));
    }
    return [];
};

/**
 * Maps API response to OperationLoanFormPage form data (CPRFFormData).
 */
export const mapLoanApiResponseToForm = (data: ContractApiResponse) => {
    const schema = data.operation_dictionary || {};
    return {
        type: schema.type || 'CPRF',
        operation_number: data.operation_number || '',
        sequencial_number: data.sequencial_number || 0,
        nominal_value: data.nominal_value || 0,
        loan_amount: data.loan_amount || 0,
        approved_value: data.approved_value?.toString() || '',
        interest_rate: '',
        expiration_date: data.expiration_date ? formatISOToDate(data.expiration_date) : '',
        emission_date: data.emission_date ? formatISOToDate(data.emission_date) : '',
        approved_on: data.approved_on ? formatISOToDate(data.approved_on) : '',
        external_link: schema.external_link || '',
        credit_product_type_name: data.credit_product_type_name || schema.credit_product_type_name || '',
        receiving_account_name: data.receiving_account_name || schema.receiving_account_name || '',
        operation_uf: schema.operation_uf || '',
        operation_city: schema.operation_city || '',
        operation_dictionary: {
            type: schema.type || 'CPRF',
            nominal_value: schema.nominal_value || 0,
            requested_value: schema.requested_value || 0,
            interest_rate: schema.interest_rate || 0,
            emission_date: schema.emission_date ? formatISOToDate(schema.emission_date) : '',
            status: schema.status || 1,
            guarantee_value: schema.guarantee_value || null,
            measures: {
                referency: schema.measures?.referency || 0,
                aggregation_unit_measure: schema.measures?.aggregation_unit_measure || '',
                amount_in_aggregation_unit: schema.measures?.amount_in_aggregation_unit || 0,
                unit_measure: schema.measures?.unit_measure || ''
            },
            commodity: {
                harvest_year_reference: schema.commodity?.harvest_year_reference || '',
                price: schema.commodity?.price || 0,
                quality: schema.commodity?.quality || '',
                description: schema.commodity?.description || '',
                amount: schema.commodity?.amount || 0,
                quotation: {
                    date: schema.commodity?.quotation?.date ? formatISOToDate(schema.commodity.quotation.date) : '',
                    source: schema.commodity?.quotation?.source || '',
                    city: schema.commodity?.quotation?.city || '',
                    province_name: schema.commodity?.quotation?.province_name || '',
                    province_initials: schema.commodity?.quotation?.province_initials || ''
                }
            },
            crop_type: {
                id: schema.crop_type?.id || 0,
                name: schema.crop_type?.name || ''
            },
            main_risk: (() => {
                const mr = schema.main_risk as any;
                if (mr && typeof mr === 'object' && mr.uuid) return { uuid: mr.uuid, name: mr.name || '' };
                if (mr && typeof mr === 'string') return { uuid: '', name: mr };
                const assignor = data.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR');
                return assignor ? { uuid: assignor.person_id || '', name: assignor.name || '' } : { uuid: '', name: '' };
            })(),
            guarantors: (Array.isArray(schema.guarantors) ? schema.guarantors : schema.guarantors?.items || []).map((g: any) => ({
                uuid: g.uuid || g.person_id,
                name: g.name,
                document: g.document,
                entity_type: g.entity_type,
                guarantee_percentage: g.guarantee_percentage || 0,
                participant_type_name: g.participant_type || g.participant_type_name
            })),
            installment: schema.installment || { total: 0, lastDueDate: '', quantity: 0, items: [] },
            fees: schema.fees || { quantity: 0, items: [] }
        },
        base_calculation: schema.base_calculation || {
            tir_annual: null, tir_monthly: null, spread_annual: null,
            vpl: null, vpl_margin: null, duration_days: null
        },
        assignor_billing_address: (schema as any).assignor_billing_address || undefined
    };
};

/**
 * Maps API response to AcknowledgementDebtPage form data.
 */
export const mapAcknowledgementDebtApiResponseToForm = (data: ContractApiResponse, currentForm: AcknowledgementDebtForm) => {
    const schema = data.operation_dictionary || {};
    const assignorPart = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'ASSIGNOR');
    const recipientParticipant = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'RECIPIENT');

    return {
        form: {
            ...currentForm,
            operation_number: data.operation_number || '',
            status: data.status || '',
            sequencial_number: data.sequencial_number || 0,
            assignor: assignorPart?.name || data.assignor || '',
            recipient: recipientParticipant?.name || data.recipient || '',
            originator: data.originator || '',
            with_recourse: data.with_recourse || false,
            operation_uf: schema.operation_uf || '',
            operation_city: schema.operation_city || '',
            credit_product_type_name: data.credit_product_type_name || schema.credit_product_type_name || '',
            receiving_account_name: data.receiving_account_name || schema.receiving_account_name || '',
            total_amount: schema.total_amount || 0,
            nominal_value: data.nominal_value || 0,
            loan_amount: data.loan_amount || 0,
            expiration_date: isoToDateBR(schema.expiration_date),
            emission_date: isoToDateBR(data.emission_date || schema.emission_date),
            approved_on: isoToDateBR(schema.approved_on),
            external_link: schema.external_link || '',
            rebate_amount: schema.rebate_amount || 0,
            description_product: schema.description_product || { name: '', id: '' },
            original_contract_discharged: Array.isArray(schema.original_contract_discharged) ? schema.original_contract_discharged : [],
            collaterals: buildCollateralsFromSchema(schema),
            disbursement: {
                totalAmount: (schema as any).disbursement?.totalAmount || schema.total_amount || data.nominal_value || 0,
                disbursements: (schema as any).disbursement?.disbursements?.map((d: any) => ({
                    amount: d.amount || 0,
                    date: formatDateWithoutTimezone(d.date)
                })) || []
            },
            installments: (schema as any).installment?.items?.map((inst: any, index: number) => ({
                number: index + 1,
                value: inst.value || inst.amount || 0,
                dueDate: formatDateWithoutTimezone(inst.dueDate),
                format: inst.format || 'VALOR',
                business_reference: inst.business_reference || '',
                document_id: inst.document_id || '',
                document_title: inst.document_title || '',
                receivable_installment: inst.receivable_installment || '',
                debtor_name: inst.debtor_name || '',
                debtor_cpf_cnpj: inst.debtor_cpf_cnpj || '',
            })) || [],
            guarantee_value: schema.guarantee_value || null,
            guarantors: loadGuarantorsFromParticipants(data.participants),
            main_risk: (() => {
                const mr = schema.main_risk as any;
                if (mr && typeof mr === 'object' && mr.uuid) return { uuid: mr.uuid, name: mr.name || '' };
                if (mr && typeof mr === 'string') return { uuid: '', name: mr };
                const assignor = data.participants?.find((p: any) => p.participant_type_name === 'ASSIGNOR');
                return assignor ? { uuid: assignor.person_id || '', name: assignor.name || '' } : { uuid: '', name: '' };
            })(),
            fees: loadFees(schema.fees),
            artifacts: schema.artifacts || [],
            signed_contract_file: schema.signed_contract_file || { file_id: '', file_name: '' },
            base_calculation: (schema as any).base_calculation || {
                tir_annual: null, tir_monthly: null, spread_annual: null,
                vpl: null, vpl_margin: null, duration_days: null
            },
            billing_config: (schema as any).billing_config || EMPTY_BILLING_CONFIG,
            assignor_billing_address: (schema as any).assignor_billing_address || undefined
        },
        assignorPart,
        recipientParticipant
    };
};

/**
 * Maps API response to StructuredOperationPage form data.
 */
export const mapStructuredOperationApiResponseToForm = (
    data: ContractApiResponse,
    currentForm: StructuredOperationForm,
    reprocessInstallmentNumbers: (items: any[]) => any[]
) => {
    const schema = data.operation_dictionary || {};
    const assignorPart = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'ASSIGNOR');
    const recipientParticipant = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'RECIPIENT');

    return {
        form: {
            ...currentForm,
            operation_number: data.operation_number || '',
            status: data.status || '',
            sequencial_number: data.sequencial_number || 0,
            assignor: assignorPart?.name || data.assignor || '',
            recipient: recipientParticipant?.name || data.recipient || '',
            originator: data.originator || '',
            with_recourse: data.with_recourse || false,
            operation_uf: schema.operation_uf || '',
            operation_city: schema.operation_city || '',
            credit_product_type_name: data.credit_product_type_name || schema.credit_product_type_name || '',
            receiving_account_name: data.receiving_account_name || schema.receiving_account_name || '',
            total_amount: schema.total_amount || 0,
            nominal_value: data.nominal_value || 0,
            loan_amount: data.loan_amount || 0,
            expiration_date: isoToDateBR(schema.expiration_date),
            emission_date: isoToDateBR(data.emission_date || schema.emission_date),
            approved_on: isoToDateBR(schema.approved_on),
            external_link: schema.external_link || '',
            rebate_amount: schema.rebate_amount || 0,
            description_product: schema.description_product || { name: '', id: '' },
            collaterals: buildCollateralsFromSchema(schema),
            disbursement: {
                totalAmount: (schema as any).disbursement?.totalAmount || schema.total_amount || data.nominal_value || 0,
                disbursements: (schema as any).disbursement?.disbursements?.map((d: any) => ({
                    amount: d.amount || 0,
                    date: formatDateWithoutTimezone(d.date)
                })) || []
            },
            harvest_year: (data as any).harvest_year || (schema as any).harvest_year || '',
            installments: reprocessInstallmentNumbers(
                (schema as any).installment?.items?.map((inst: any, index: number) => ({
                    number: index + 1,
                    value: inst.value || inst.amount || 0,
                    dueDate: formatDateWithoutTimezone(inst.dueDate),
                    format: inst.format || 'VALOR',
                    business_reference: inst.business_reference || '',
                    document_id: inst.document_id || '',
                    document_title: inst.document_title || '',
                    receivable_installment: inst.receivable_installment || '',
                    debtor_name: inst.debtor_name || '',
                    debtor_cpf_cnpj: inst.debtor_cpf_cnpj || '',
                })) || []
            ),
            guarantee_value: schema.guarantee_value || null,
            guarantors: loadGuarantorsFromParticipants(data.participants),
            main_risk: (() => {
                const mr = schema.main_risk as any;
                if (mr && typeof mr === 'object' && mr.uuid) return { uuid: mr.uuid, name: mr.name || '' };
                if (mr && typeof mr === 'string') return { uuid: '', name: mr };
                return assignorPart ? { uuid: assignorPart.person_id || '', name: assignorPart.name || '' } : { uuid: '', name: '' };
            })(),
            fees: loadFees(schema.fees),
            artifacts: schema.artifacts || [],
            signed_contract_file: schema.signed_contract_file || { file_id: '', file_name: '' },
            base_calculation: (schema as any).base_calculation || {
                tir_annual: null, tir_monthly: null, spread_annual: null,
                vpl: null, vpl_margin: null, duration_days: null
            },
            billing_config: (schema as any).billing_config || EMPTY_BILLING_CONFIG,
            assignor_billing_address: (schema as any).assignor_billing_address || undefined
        },
        assignorPart,
        recipientParticipant
    };
};

/**
 * Maps API response to ReceivablesAssignmentPage form data.
 * Receivables and contracts go to form root level (they are the main asset, not collateral).
 */
export const mapReceivablesAssignmentApiResponseToForm = (data: ContractApiResponse, currentForm: Record<string, any>) => {
    const schema = data.operation_dictionary || {};
    const rawData = data as any;
    const rawSchema = schema as any;
    const assignorParticipant = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'ASSIGNOR');
    const recipientParticipant = data.participants?.find((p: ParticipantResponse) => p.participant_type_name === 'RECIPIENT');

    // Receivables e contratos-como-recebíveis são ativos principais — extrair para o nível raiz
    const receivables = extractReceivables(schema);
    const contractsAsReceivablesState = extractContractsAsReceivablesFromSchema(schema);
    const contractsAsReceivablesItems = contractsAsReceivablesState?.items || [];
    const contractsAsReceivablesTotal = contractsAsReceivablesItems.reduce((sum: number, i: any) => sum + (i.contract_value || 0), 0);

    // Collaterals contém apenas garantias adicionais
    const collaterals = buildCollateralsOnlyFromSchema(schema);

    return {
        form: {
            ...currentForm,
            type: schema.type || 'RECEIVABLES_ASSIGNMENT',
            operation_number: data.operation_number || '',
            status: data.status || '',
            sequencial_number: data.sequencial_number || 0,
            assignor: assignorParticipant?.name || '',
            recipient: recipientParticipant?.name || '',
            originator: data.originator || '',
            with_recourse: data.with_recourse || false,
            credit_product_type_name: data.credit_product_type_name || schema.credit_product_type_name || '',
            receiving_account_name: data.receiving_account_name || schema.receiving_account_name || '',
            assignment_type_name: rawData.assignment_type || '',
            created_at: formatDateWithoutTimezone(rawData.created_at) || new Date().toLocaleDateString('pt-BR'),
            expiration_date: formatLocalDateToBR(schema.expiration_date),
            emission_date: formatLocalDateToBR(data.emission_date || schema.emission_date),
            approved_on: formatLocalDateToBR(data.approved_on || schema.approved_on),
            total_amount: schema.total_amount || data.nominal_value || 0,
            loan_amount: data.loan_amount || 0,
            external_link: schema.external_link || '',
            rebate_amount: schema.rebate_amount || 0,
            guarantee_percentage: rawSchema.guarantee_percentage || (assignorParticipant as any)?.guarantee_percentage || 0,
            receivables: receivables || { items: [], total: 0, quantity: 0 },
            contracts_as_receivables: { items: contractsAsReceivablesItems, total: contractsAsReceivablesTotal, quantity: contractsAsReceivablesItems.length },
            collaterals,
            disbursement: {
                disbursements: rawSchema.disbursement?.disbursements || [],
                totalAmount: rawSchema.disbursement?.totalAmount || schema.total_amount || data.nominal_value || 0
            },
            base_calculation: schema.base_calculation || {
                tir_annual: null, tir_monthly: null, spread_annual: null,
                vpl: null, vpl_margin: null, duration_days: null
            },
            billing_config: rawSchema.billing_config || EMPTY_BILLING_CONFIG,
            installments: formatInstallments(rawSchema.installment) || [],
            assignor_billing_address: (schema as any).assignor_billing_address || undefined,
            main_risk: (() => {
                const mr = schema.main_risk as any;
                if (mr && typeof mr === 'object' && mr.type) {
                    return { type: mr.type, uuid: mr.uuid || '', name: mr.name || '', document: mr.document || '' };
                }
                // Default: se não houver risco principal informado, assume o Sacado.
                return { type: 'DRAWEE', uuid: '', name: '', document: '' };
            })()
        },
        assignorParticipant,
        recipientParticipant
    };
};
